import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { formatMoney } from '@/shared/lib/format/money';
import { useWithdrawModalState } from './useWithdrawModalState';
import { AddBankForm } from './sections/AddBankForm';
import { BankListStep } from './sections/BankListStep';
import { AmountStep } from './sections/AmountStep';
import { ModalFooter } from './sections/ModalFooter';
import type { WithdrawModalProps } from './WithdrawModal.types';

const ADD_BANK_FORM_ID = 'add-bank-form';

export function WithdrawModal(props: WithdrawModalProps) {
  const { open, currentBalance } = props;
  const {
    step,
    adding,
    setAdding,
    selectedBankId,
    amount,
    setAmount,
    error,
    setError,
    banks,
    banksLoading,
    isAddingBank,
    deleteBank,
    isWithdrawing,
    register,
    handleSubmit,
    formErrors,
    close,
    onAddBank,
    selectBank,
    proceedWithBank,
    submitWithdraw,
    goBack,
    breakdown,
    net,
    totalDeduction,
    isCalculatingCharges,
  } = useWithdrawModalState(props);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl border border-[var(--dash-line)] bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--dash-line)] px-5 py-4">
          <div className="text-left">
            <h2 className="text-base font-bold text-[var(--dash-ink)]">
              {step === 'bank' ? 'Withdraw' : 'Withdraw amount'}
            </h2>
            <p className="text-xs text-[var(--dash-muted)]">
              {step === 'bank'
                ? 'Choose or add a bank account'
                : `Balance · ${formatMoney(currentBalance)}`}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-md p-1 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 text-left">
          {step === 'bank' ? (
            banksLoading || !adding ? (
              <BankListStep
                banks={banks}
                isLoading={banksLoading}
                selectedBankId={selectedBankId}
                onSelectBank={selectBank}
                onDeleteBank={deleteBank}
                onAddNew={() => setAdding(true)}
                error={error}
              />
            ) : (
              <AddBankForm
                formId={ADD_BANK_FORM_ID}
                register={register}
                handleSubmit={handleSubmit}
                errors={formErrors}
                onSubmit={onAddBank}
              />
            )
          ) : (
            <AmountStep
              amount={amount}
              onAmountChange={(v) => {
                setAmount(v);
                setError('');
              }}
              error={error}
              breakdown={breakdown}
              net={net}
              totalDeduction={totalDeduction}
              isCalculatingCharges={isCalculatingCharges}
            />
          )}
        </div>

        <ModalFooter
          step={step}
          adding={adding && !banksLoading}
          hasBanks={banks.length > 0}
          formId={ADD_BANK_FORM_ID}
          isAddingBank={isAddingBank}
          isWithdrawing={isWithdrawing}
          onBack={goBack}
          onCancel={close}
          onContinueWithBank={proceedWithBank}
          onSubmitWithdraw={submitWithdraw}
        />
      </div>
    </div>,
    document.body,
  );
}
